<template>
  <div class="container">
    <div class="py-4 text-center">
      <h2>Early Retirement Calculator</h2>
      <p class="lead">A rough guide to calculating your retirement age</p>
    </div>

    <div class="row">
      <div class="col-md-12">
        <form>
          <div class="row">
            <div class="col-md-4 mb-3">
              <label>Annual savings</label>
              <div class="input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text">&#163;</span>
                </div>
                <input
                  class="form-control"
                  aria-describedby="annualSavingsHelp"
                  type="number"
                  min="0"
                  v-model.number="annualSavings"
                  oninput="validity.valid||(value='');"
                >
              </div>
              <small
                id="annualSavingsHelp"
                class="form-text text-muted"
              >Amount you save each year (from all sources such as SIPPs, ISAs and employer pension contributions)</small>
            </div>
            <div class="col-md-4 mb-3">
              <label>Annual expenses</label>
              <div class="input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text">&#163;</span>
                </div>
                <input
                  class="form-control"
                  aria-describedby="annualExpensesHelp"
                  type="number"
                  min="0"
                  v-model.number="annualExpenses"
                  oninput="validity.valid||(value='');"
                >
              </div>
              <small
                id="annualExpensesHelp"
                class="form-text text-muted"
              >Amount you will spend each year in retirement</small>
            </div>
            <div class="col-md-4 mb-3">
              <label>Current value of investments</label>
              <div class="input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text">&#163;</span>
                </div>
                <input
                  class="form-control"
                  aria-describedby="currentValueHelp"
                  type="number"
                  min="0"
                  v-model.number="currentValue"
                  oninput="validity.valid||(value='');"
                >
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-4 mb-3">
              <label>Withdrawal rate</label>
              <div class="input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text">%</span>
                </div>
                <input
                  class="form-control"
                  aria-describedby="withdrawalRateHelp"
                  type="number"
                  min="0"
                  max="100"
                  v-model.number="withdrawalRate"
                  oninput="validity.valid||(value='');"
                >
              </div>
              <small id="withdrawalRateHelp" class="form-text text-muted">
                Set to 3.2% as a safe withdrawal rate (taken from
                <a
                  href="http://media.morningstar.com/uk/MEDIA/Comprehensive_update_on_the_Safe_Withdrawal_Rate.pdf"
                  target="blank"
                >MorningStar's research</a>)
              </small>
            </div>
            <div class="col-md-4 mb-3">
              <label>Annual return on investment</label>
              <div class="input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text">%</span>
                </div>
                <input
                  class="form-control"
                  aria-describedby="annualReturnHelp"
                  type="number"
                  min="0"
                  max="100"
                  v-model.number="annualReturn"
                  oninput="validity.valid||(value='');"
                >
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
    <hr class="mb-5">
    <div class="row">
      <div class="col-md-6 offset-md-3">
        <ul class="list-group mb-3">
          <li class="list-group-item d-flex justify-content-between lh-condensed">
            <div>
              <h6 class="my-0">Years to retirement</h6>
            </div>
            <span class="text-muted" v-cloak>{{yearsToFi}}</span>
          </li>
          <li class="list-group-item d-flex justify-content-between lh-condensed">
            <div>
              <h6 class="my-0">Pot required</h6>
            </div>
            <span class="text-muted" v-cloak>£{{potRequired}}</span>
          </li>
        </ul>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <footer class="my-5 pt-5 text-muted text-center text-small">
          <p class="mb-1">
            Made by
            <a href="https://www.matt-darby.co.uk/" target="_blank">Matt Darby</a>
          </p>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "EarlyRetirementCalculator",
  data() {
    return {
      annualSavings: "",
      annualExpenses: "",
      currentValue: 0,
      withdrawalRate: 3.2,
      annualReturn: 4.5
    };
  },
  computed: {
    savingsRate: function() {
      return (
        (this.annualSavings / (this.annualSavings + this.annualExpenses)) * 100
      );
    },
    potRequired: function() {
      var pot = Math.round(this.annualExpenses / (this.withdrawalRate / 100));
      if (
        pot == null ||
        isNaN(pot) ||
        !isFinite(pot) ||
        !(this.annualSavings > 0 && this.annualExpenses > 0)
      ) {
        return "--";
      } else {
        return pot;
      }
    },
    yearsToFi: function() {
      var years = (
        NPER(
          this.annualReturn / 100 / 12,
          this.annualSavings / 12,
          this.currentValue,
          -this.potRequired
        ) / 12
      ).toFixed(1);
      if (
        years == null ||
        isNaN(years) ||
        !(this.annualSavings > 0 && this.annualExpenses > 0)
      ) {
        return "--";
      } else {
        return years;
      }
    }
  }
};

function NPER(rate, payment, present, future, type) {
  var type = typeof type === "undefined" ? 0 : type;
  var future = typeof future === "undefined" ? 0 : future;
  rate = eval(rate);
  var num = payment * (1 + rate * type) - future * rate;
  var den = present * rate + payment * (1 + rate * type);
  return Math.log(num / den) / Math.log(1 + rate);
}
</script>